<template>
  <div class="flex flex-col justify-end h-screen">
    <!-- Loading -->
    <Loading v-if="loading" class="p-8" />

    <template v-else>
      <!-- Message Modal -->
      <Modal :open.sync="showMessageModal">
        <form @submit.prevent="sendMessage">
          <textarea ref="messageInput" id="messageInput" name="message" cols="30" rows="2" placeholder="Write your message..." class="block w-full p-4 rounded-t focus:ring focus:outline-none" v-model="message"></textarea>
          <div class="flex justify-between p-3 bg-gray-100 rounded-b">
            <button type="button" class="btn btn-secondary" @click="showMessageModal = false">
              Cancel
            </button>
            <button type="submit" class="btn btn-accent" v-submitting="submitting"  :disabled="message.length < 1">
              Send
            </button>
          </div>
        </form>
      </Modal>

      <!-- Map -->
      <div class="fixed w-screen h-screen bg-gray-700">
        <MglMap 
          ref="map"
          class="w-full h-full"
          :access-token="accessToken"
          :map-style="mapStyle"
          :interactive="false"
          @load="mapLoaded">
          <MglMarker :coordinates="orderCoordinates" anchor="bottom">
            <div slot="marker">
              <svg class="w-6 text-accent" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="currentColor" d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"></path><circle cx="192" cy="192" fill="#fff" r="80" /></svg>
            </div>
          </MglMarker>
          <MglMarker :coordinates="vehicleCoordinates">
            <div slot="marker">
              <div class="relative flex items-center justify-center w-6 h-6">
                <div class="absolute w-3 h-3 transform -translate-x-1/2 -translate-y-1/2 bg-opacity-100 rounded-full bg-accent top-1/2 left-1/2"></div>
                <div class="w-5 h-5 rounded-full bg-accent animate-ping"></div>
              </div>
            </div>
          </MglMarker>
        </MglMap>
      </div>

      <!-- Info & Controls -->
      <div class="relative z-20 max-w-lg bg-white border-t shadow-lg sm:rounded-lg sm:m-4">
        <button class="flex items-center justify-between w-full p-4 text-left" @click="expand = !expand">
          <div class="flex items-center flex-none">
            <div v-if="portal.logo" class="flex items-center justify-center w-16 h-16 p-1 bg-white rounded">
              <img :src="portal.logo" class="object-cover">
            </div>
            <div v-else class="flex items-center justify-center w-16 h-16 text-2xl font-bold text-white rounded-full bg-accent">
              {{ senderInitials }}
            </div>
            <div class="ml-4 leading-tight">
              <div class="font-bold">{{ driverName }}</div>
              <div class="text-gray-500">{{ order.customer_name || account.name }}</div>
              <div class="font-medium text-accent">{{ eta }}</div>
            </div>
          </div>
          <div class="pr-4 text-gray-400">
            <fa :icon="['far', 'chevron-up']" size="lg" class="transition duration-200 ease-linear transform" :class="{ 'rotate-180': expand }" />
          </div>
        </button>
        <Collapse :active="expand" :duration="250">
          <div class="grid gap-3">
            <div class="flex items-start px-4 leading-tight" v-if="vehicle">
              <div class="w-16 mr-4 text-right text-gray-400">
                <fa :icon="['fas', 'car']" fixed-width />
              </div>
              <div class="text-sm">
                <div class="font-bold">
                  {{ vehicle.objectname }}
                </div>
                <div class="text-gray-500">
                  {{ vehicle.licenseplatenumber }}
                </div>
              </div>
            </div>
            <div class="flex items-start px-4 leading-tight" v-if="order.formatted_address">
              <div class="w-16 mr-4 text-right text-gray-400">
                <fa :icon="['fas', 'map-marker-alt']" fixed-width />
              </div>
              <div class="text-sm">
                <div class="font-bold">
                  {{ order.formatted_address.split(',')[0] }}
                </div>
                <div class="text-gray-500">
                  {{ order.formatted_address.split(',')[1] }}
                </div>
              </div>
            </div>
            <div class="flex items-center justify-around p-4">
              <button 
                v-if="account.tracking_link_settings.message_driver_enabled"
                class="w-full btn btn-accent" 
                @click="openMessageModal()">
                <fa :icon="['far', 'comment']" class="mr-1" size="sm" /> Message Driver
              </button>
            </div>
          </div>
        </Collapse>
      </div>
    </template>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { formatDistanceToNow } from 'date-fns'
import { MglMap, MglMarker } from 'vue-mapbox'

export default {
  name: 'TrackDriver',

  metaInfo: {
    title: 'Track Your Order'
  },

  components: {
    MglMap,
    MglMarker
  },

  async created () {
    await this.fetchOrder()
  },

  destroyed () {
    clearInterval(this.poll)
  },

  computed: {
    ...mapState(['portal']),
    
    orderCoordinates () {
      if (this.order) {
        return [Number(this.order.longitude), Number(this.order.latitude)]
      } else {
        return [0, 0]
      }
    },

    vehicleCoordinates () {
      if (this.vehicle) {
        return [Number(this.vehicle.longitude) / 1000000 , Number(this.vehicle.latitude) / 1000000]
      } else {
        return [0, 0]
      }
    },

    eta () {
      if (this.order && this.order.eta) {
        return formatDistanceToNow(new Date(this.order.eta))
      } else {
        return ''
      }
    },

    driverName () {
      if (this.driver) {
        return this.driver.name
      }
    },

    senderInitials () {
      // this.order && this.order.customer_name ||
      if (this.account && this.account.name) {
        let name = this.order.customer_name || this.account.name
        let initials = name.split(' ').map(name => name.substr(0, 1))
        return initials.join('')
      } else {
        return ''
      }
    }
  },

  data () {
    return {
      loading: true,
      account: {},
      driver: {},
      order: {},
      vehicle: {},
      poll: null,
      expand: true,
      accessToken: 'pk.eyJ1IjoiZmxlZXRiZWFjb24iLCJhIjoiY2puZXczdHhmMHFqeDNrbGZ3dGNmbGU2cCJ9.dZ0JMOD5222pAk-pR6wzYQ',
      mapStyle: 'mapbox://styles/fleetbeacon/ckcw7i47m15m71imu9z9329lr',
      showMessageModal: false,
      message: '',
      submitting: false
    }
  },

  methods: {
    openMessageModal () {
      this.showMessageModal = true
      setTimeout(() => {
        this.$refs.messageInput.focus()
      }, 50)
    },

    fitBounds () {
      this.$refs.map.map.fitBounds([this.orderCoordinates, this.vehicleCoordinates], {
        linear: true,
        maxZoom: 16,
        padding: {
          top: 64,
          left: 64,
          bottom: 360,
          right: 64
        }
      })
    },

    mapLoaded () {
      this.pollLocation()
    },

    async fetchOrder () {
      try {
        const response = await this.$api.get(`tracking-links/${this.$route.params.trackingCode}/location`)
        
        this.order = response.data.order
        this.account = this.order.account
        this.driver = this.order.driver
        this.vehicle = this.order.vehicle
        this.loading = false

        this.poll = setInterval(function () {
          this.pollLocation()
        }.bind(this), 5000)
      } catch (error) {
        if (error.response.status === 403) {
          this.$router.push({ 
            name: 'TrackingLinkStatus', 
            params: { 
              trackingCode: this.$route.params.trackingCode 
            } 
          })
          return
        }
        
        throw Error(error)
      }
    },

    async pollLocation () {
      try {
        const response = await this.$api.get(`tracking-links/${this.$route.params.trackingCode}/location`)

        this.order = response.data.order
        this.account = this.order.account
        this.driver = this.order.driver
        this.vehicle = this.order.vehicle
        
        this.fitBounds()
      } catch (error) {
        if (response.status === 403) {
          this.$router.push({ 
            name: 'TrackingLinkStatus', 
            params: { 
              trackingCode: this.$route.params.trackingCode 
            } 
          })
          return
        }

        throw Error(error)
      }
    },

    async sendMessage () {
      try {
        this.submitting = true
        
        await this.$api.post(`tracking-links/${this.$route.params.trackingCode}/message`, {
          message: this.message
        })
        
        this.message = ''
        this.showMessageModal = false
        this.$toasted.show('Message Sent')
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.message)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>